import { useEffect, useMemo, type FC, type PropsWithChildren } from 'react';
import {
  ThemeProvider as MuiThemeProvider,
  useColorScheme,
} from '@mui/material';
import { isNil } from 'lodash';

import { Theme } from '#packages/web/theme';

import { useLogger } from '#apps/client-web/app/hooks/use-logger';

import { useUserSettings } from '../hooks/use-user-settings';

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const logger = useLogger('features/user-settings/providers/theme-provider');

  const { getUserSettingsQuery } = useUserSettings();

  const themeName = useMemo(() => {
    logger.debug(
      {
        isSuccess: getUserSettingsQuery.isSuccess,
        data: getUserSettingsQuery.data,
      },
      'User settings query changed, determining theme name',
    );

    // not ready yet
    if (!getUserSettingsQuery.isSuccess) {
      return undefined;
    }

    return getUserSettingsQuery.data.theme.name;
  }, [getUserSettingsQuery.isSuccess, getUserSettingsQuery.data]);

  const muiTheme = useMemo(() => {
    logger.debug({ name: themeName }, 'Theme name changed, creating theme');

    // not ready yet
    if (isNil(themeName)) {
      return undefined;
    }

    logger.debug({ name: themeName }, 'Theme name changed, creating theme');

    return Theme.create(themeName);
  }, [themeName]);

  const { setMode } = useColorScheme();
  // Sync MUI color scheme mode with theme appearance
  useEffect(() => {
    logger.debug(
      {
        isSuccess: getUserSettingsQuery.isSuccess,
        data: getUserSettingsQuery.data,
      },
      'User settings query changed, determining theme name',
    );

    // not ready yet
    if (!getUserSettingsQuery.isSuccess) {
      return undefined;
    }

    const appearance = getUserSettingsQuery.data.theme.appearance;
    logger.debug(
      { appearance },
      'User settings changed, updating MUI color scheme mode',
    );
    setMode(appearance);
  }, [getUserSettingsQuery.isSuccess, getUserSettingsQuery.data, setMode]);

  // not ready yet
  if (isNil(muiTheme)) {
    return null;
  }

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
};
